/**
* Public price-display policy.
*
* The firm quotes per case (page count, language pair, turnaround,
* legalisation route), so no fixed retail price is published on the site.
* Visitors are directed to phone / LINE / email for a quote instead.
*
* Keep this the single source of truth: presentation components read
* SHOW_PUBLIC_PRICES and QUOTE_LABEL rather than printing numbers.
*/
export const SHOW_PUBLIC_PRICES = false;

export type QuoteLocale = "th" | "en" | "zh" | "ja" | "ko" | "vi" | "id";

/** Short CTA used where a price used to appear. */
export const QUOTE_LABEL: Record<QuoteLocale, string> = {
  th: "สอบถามราคา",
  en: "Request a quote",
  zh: "咨询报价",
  ja: "お見積り",
  ko: "견적 문의",
  vi: "Nhận báo giá",
  id: "Minta penawaran",
};

/** Longer helper line: how to reach us for the quote. */
export const QUOTE_HINT: Record<QuoteLocale, string> = {
  th: "โทร · LINE · อีเมล",
  en: "Phone · LINE · Email",
  zh: "电话 · LINE · 邮件",
  ja: "電話 · LINE · メール",
  ko: "전화 · LINE · 이메일",
  vi: "Điện thoại · LINE · Email",
  id: "Telepon · LINE · Email",
};

export function quoteLabel(locale: QuoteLocale = "th") {
  return QUOTE_LABEL[locale] ?? QUOTE_LABEL.th;
}

export function quoteHint(locale: QuoteLocale = "th") {
  return QUOTE_HINT[locale] ?? QUOTE_HINT.th;
}

/**
* Removes the "THB {lo}-{hi}" / "฿{lo}-฿{hi}" fragment from a copy
* template so headings stay grammatical once prices are hidden.
*/
export function stripPriceRange(text: string) {
  return text
    .replace(/\s*·?\s*(THB|฿)?\s*\{lo\}\s*[-–]\s*(฿)?\{hi\}/g, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}

/**
 * Inline sentence fragment used where copy previously interpolated a number.
 * Keeps generated multi-locale prose grammatical with prices hidden.
 */
export const QUOTE_PHRASE: Record<QuoteLocale, string> = {
  th: "แจ้งค่าบริการเป็นรายเคส (โทร · LINE · อีเมล)",
  en: "quoted per case (phone · LINE · email)",
  zh: "按件报价(电话 · LINE · 邮件)",
  ja: "案件ごとの見積り(電話 · LINE · メール)",
  ko: "건별 견적 (전화 · LINE · 이메일)",
  vi: "báo giá theo từng hồ sơ (điện thoại · LINE · email)",
  id: "penawaran per kasus (telepon · LINE · email)",
};

export function quotePhrase(locale: QuoteLocale = "th") {
  return QUOTE_PHRASE[locale] ?? QUOTE_PHRASE.th;
}
